import SlyEvent from '@common/helpers/events';
import { phoneFormatter, phoneParser } from '@common/helpers/phone';
import FieldAdapter from '@react/form/components/FieldAdapter';
import {
  composeValidators,
  email,
  required,
  usPhone,
} from '@react/form/helpers/validation';
import { useToast } from '@react/shared/components/Toast';
import { getClientData } from '@sly/core/constants/localStorage';
import { DEFAULT_EMAIL_VALUE } from '@sly/core/constants/retentionPopup';
import {
  leadSourceMap,
  marketingChannelMap,
  marketingMediumMap,
} from '@sly/core/constants/uuidActions';
import { retrieveLocalStorage } from '@sly/frontend/common/helpers/localStorage';
import Dialog from '@sly/frontend/react/shared/components/Dialog';
import cx from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useQuery } from 'sly/services/api';
import { Button } from 'sly/system';

const ContactUsForm = ({
  path,
  submitButtonText,
  showLocation = false,
  onSubmit = () => {},
}) => {
  const createAction = useQuery('createUuidAction');
  const { pushSuccess: notifyInfo, pushError: notifyError } = useToast();
  const [clientData, setClientData] = useState(null);

  const [isReadOnlyEmail, setIsReadOnlyEmail] = useState(false);

  useEffect(() => {
    const clientData = getClientData();
    setClientData(clientData);
  }, []);

  const router = useRouter();

  useEffect(() => {
    setIsReadOnlyEmail(!!retrieveLocalStorage(DEFAULT_EMAIL_VALUE));
  }, []);

  const initialValues = {
    email: clientData?.email || retrieveLocalStorage(DEFAULT_EMAIL_VALUE) || '',
    firstName: clientData?.firstName || '',
    lastName: clientData?.lastName || '',
    message: router.query.message || '',
    phone: clientData?.phone || '',
    location: clientData?.zip
      ? {
          searchParams: {
            zip: clientData?.zip || '',
          },
        }
      : null,
  };

  const handleSubmit = (data, form) => {
    data = {
      ...data,
      name: `${data.firstName}${data.lastName ? ` ${data.lastName}` : ''}`,
    };
    const { phone, email: innerEmail, name, message, location } = data;
    const actionInfo = {
      phone,
      name,
      email: innerEmail,
      message,
      city: location?.searchParams?.city,
      state: location?.searchParams?.state,
      zip: location?.searchParams?.zip,
      data: {
        leadSource: leadSourceMap.ContactUs,
        channel: marketingChannelMap.OrganicMC,
        medium: marketingMediumMap.OnlineMM,
      },
    };

    return createAction({
      type: 'UUIDAction',
      attributes: {
        actionInfo,
        actionPage: path,
        actionType: showLocation
          ? 'adConsultationRequested'
          : 'consultationRequested',
      },
    })
      .then(({ body: { data: innerData } }) => {
        if (innerData) {
          const { attributes } = innerData || {};
          const event = {
            action: 'send_message',
            category: attributes?.actionType,
            label: path,
          };
          SlyEvent.getInstance().sendEvent(event);
          notifyInfo(
            'We have received your message and we will get back to you soon.',
            null,
            'line-clamp-3'
          );
          form.restart();
          onSubmit?.();
        }
      })
      .catch(({ body }) => {
        let errorMessage = 'Something went wrong';
        if (body?.errors) {
          errorMessage = body.errors[0].detail;
        } else {
          console.error(body);
        }
        notifyError(errorMessage);
      });
  };

  return (
    <div data-tp-id='gen-Form-a34f6900-868f-4261-91f3-2b59745944af'>
      <Head>
        {router?.query?.message && <meta name='robots' content='noindex' />}
      </Head>
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        data-tp-id='gen-Form-e4575b81-d867-4fdb-a6ac-d8adae572012'
      >
        {({ handleSubmit: innerHandleSubmit, submitting, invalid }) => (
          <form
            onSubmit={innerHandleSubmit}
            data-tp-id='gen-form-4f4efaf9-a57c-4fd0-8b92-3b6d92dc5962'
          >
            {showLocation && (
              <Field
                type='locationSearch'
                name='location'
                label='Location'
                component={FieldAdapter}
                validate={required}
                data-tp-id='gen-Field-bd239552-c566-4b38-a52b-e7e584cfa65d'
              />
            )}
            <div
              className={cx(
                'grid grid-flow-row gap-s',
                'mb-xxs grid-cols-[auto_auto]'
              )}
              data-tp-id='gen-div-ec91cebd-eacf-4271-8757-f2f3966f821f'
            >
              <Field
                component={FieldAdapter}
                validate={required}
                name='firstName'
                label='First name'
                type='text'
                data-tp-id='gen-Field-7b7fee9a-271c-467d-8499-00a261b6251d'
              />
              <Field
                name='lastName'
                label='Last name'
                type='text'
                component={FieldAdapter}
                validate={required}
                data-tp-id='gen-Field-bea94cf9-6966-4486-be60-4c91355958b1'
              />
            </div>
            <Field
              name='phone'
              label='Phone'
              type='phone'
              component={FieldAdapter}
              parse={phoneParser}
              format={phoneFormatter}
              validate={composeValidators(required, usPhone)}
              data-tp-id='gen-Field-07a6cc9c-2e07-43a4-8dc2-0148b9cc311b'
            />
            <Field
              name='email'
              label='Email'
              type='email'
              component={FieldAdapter}
              validate={composeValidators(required, email)}
              data-tp-id='gen-Field-ff0a51a4-afef-43db-b0bb-f38d534fd2f6'
              readOnly={isReadOnlyEmail}
            />

            {!showLocation && (
              <Field
                type='textarea'
                rows='3'
                name='message'
                label='Message'
                component={FieldAdapter}
                validate={required}
                data-tp-id='gen-Field-bd239552-c566-4b38-a52b-e7e584cfa65d'
              />
            )}

            <Button
              className={cx('mt-xs w-full')}
              type='submit'
              disabled={invalid || submitting}
              data-tp-id='gen-Button-70dfd9b4-cfdf-43e1-a53a-e5ccd4d6a4da'
            >
              {submitButtonText}
            </Button>
          </form>
        )}
      </Form>
    </div>
  );
};

ContactUsForm.propTypes = {
  path: string,
  submitButtonText: string,
};

ContactUsForm.defaultProps = {
  submitButtonText: 'Send message',
};

export default ContactUsForm;

export const ContactUsModal = ({
  submitButtonText,
  showLocation,
  headingText = 'Connect with us',
  buttonText,
}) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const close = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog closeOnOutsideClick open={open} onClose={close}>
        <Dialog.Header onClose={close}>{headingText}</Dialog.Header>
        <Dialog.Body>
          <ContactUsForm
            submitButtonText={submitButtonText}
            showLocation={showLocation}
            path={router.pathname}
            onSubmit={close}
          />
        </Dialog.Body>
      </Dialog>
      <Button onClick={() => setOpen(true)}>{buttonText}</Button>
    </>
  );
};

export const ContactUsFormWrapper = ({ submitButtonText, showLocation }) => {
  const router = useRouter();

  return (
    <ContactUsForm
      submitButtonText={submitButtonText}
      showLocation={showLocation}
      path={router.pathname}
    />
  );
};
